import { useEffect, useRef, useState } from "react";
import { streamAgent, profileApi } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { useDemoCode } from "../auth/DemoCodeProvider";

interface Msg {
  role: "user" | "assistant";
  content: string;
  streaming?: boolean;
}

export function ChatPanel({ agent }: { agent: "claude" | "gemini" | "grok" }) {
  const { user } = useAuth();
  const { code: demoCode } = useDemoCode();
  const [sessionId, setSessionId] = useState(() => cryptoId());
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [rateWarning, setRateWarning] = useState<{ remaining: number; limit: number } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function send() {
    if (!input.trim() || busy || !user) return;
    const userMsg: Msg = { role: "user", content: input };
    const draft: Msg = { role: "assistant", content: "", streaming: true };
    setMessages((m) => [...m, userMsg, draft]);
    setInput("");
    setBusy(true);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      for await (const ev of streamAgent(
        `/chat/${agent}`,
        { sessionId, message: userMsg.content },
        { idToken: user.idToken, demoCode, signal: ctrl.signal },
      )) {
        if (ev.type === "token") {
          setMessages((m) => {
            const next = m.slice();
            next[next.length - 1] = {
              ...next[next.length - 1],
              content: next[next.length - 1].content + ev.text,
            };
            return next;
          });
        } else if (ev.type === "error") {
          setMessages((m) => {
            const next = m.slice();
            next[next.length - 1] = { role: "assistant", content: `⚠ ${ev.message}` };
            return next;
          });
        } else if (ev.type === "rate-limit-warning") {
          setRateWarning({ remaining: ev.remaining, limit: ev.limit });
        }
      }
    } catch (err) {
      const e = err as Error & { kind?: string; limit?: number; resetAt?: string };
      if ((err as DOMException).name !== "AbortError") {
        let msg = "⚠ Request failed.";
        if (e.kind === "rate-limit") {
          msg = `⚠ Daily limit reached (${e.limit ?? "limit"} messages). Resets at ${
            e.resetAt ? new Date(e.resetAt).toLocaleString() : "midnight UTC"
          }.`;
        } else if (e.kind === "unauthorized") {
          msg = "⚠ Invalid or missing demo code. Open Settings to enter the invite code.";
        } else if (e.kind === "demo-unavailable") {
          msg = "⚠ Demo is paused right now — please try again later.";
        }
        setMessages((m) => {
          const next = m.slice();
          next[next.length - 1] = { role: "assistant", content: msg };
          return next;
        });
      }
    } finally {
      setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, streaming: false } : x)));
      setBusy(false);
      abortRef.current = null;
    }
  }

  function cancel() {
    abortRef.current?.abort();
  }

  async function endSession() {
    if (!user) return;
    await profileApi(user.idToken).endSession(agent, sessionId).catch(() => {});
    setSessionId(cryptoId());
    setMessages([]);
  }

  function clearChat() {
    setMessages([]);
  }

  return (
    <section className="chat">
      <div className="chat__header">
        <h2>{label(agent)}</h2>
        <div className="chat__actions">
          <button onClick={clearChat} disabled={busy}>Clear Chat</button>
          <button onClick={endSession} disabled={busy}>End Session</button>
        </div>
      </div>
      {rateWarning && (
        <div className="chat__ratewarn" role="status">
          Heads up — {rateWarning.remaining} of {rateWarning.limit} daily messages left.
        </div>
      )}
      <div className="chat__messages">
        {messages.length === 0 && <div className="chat__empty">Ask {label(agent)} anything.</div>}
        {messages.map((m, i) => (
          <div key={i} className={`msg msg--${m.role}`}>
            <div className="msg__sender">{m.role === "user" ? "You" : label(agent)}</div>
            <div className="msg__content">
              {m.content}
              {m.streaming && <span className="cursor">▍</span>}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <form
        className="chat__input"
        onSubmit={(e) => { e.preventDefault(); send(); }}
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Message ${label(agent)}…`}
          rows={2}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          disabled={busy && !abortRef.current}
        />
        {busy
          ? <button type="button" className="btn-cancel" onClick={cancel}>Stop</button>
          : <button type="submit" className="btn-send" disabled={!input.trim()}>Send</button>}
      </form>
    </section>
  );
}

function label(a: string): string {
  return a.charAt(0).toUpperCase() + a.slice(1);
}
function cryptoId(): string {
  return (globalThis.crypto?.randomUUID?.() ?? String(Date.now())).slice(0, 32);
}
